import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type User } from '@chicare/shared';

import { api } from './api';

/**
 * Who is signed in.
 *
 * One query, cached, shared by every screen that needs to know. The token
 * itself lives in the client's `TokenStore` (localStorage on web, AsyncStorage
 * on React Native) — this hook only ever deals in the *user*, so nothing above
 * it has to know how the token is stored.
 */

export const sessionKeys = {
  me: () => ['session', 'me'] as const,
};

export interface SessionState {
  user: User | null;
  isLoading: boolean;
  /** True once we know for certain that nobody is signed in. */
  isUnauthenticated: boolean;
  isAdmin: boolean;
}

export function useSession(): SessionState {
  const { data, isLoading, error, isFetched } = useQuery({
    queryKey: sessionKeys.me(),
    queryFn: ({ signal }) => api.me(signal),
    // A 401 is a definitive answer, not a transient failure. Retrying it just
    // delays the login screen the user is waiting for.
    retry: (failureCount, err) =>
      !(err instanceof ApiError && err.isUnauthenticated) && failureCount < 2,
    staleTime: 5 * 60_000,
  });

  const isUnauthenticated =
    isFetched && (error instanceof ApiError ? error.isUnauthenticated : !data);

  return {
    user: data ?? null,
    isLoading,
    isUnauthenticated,
    isAdmin: data?.role === 'admin' || data?.role === 'super_admin',
  };
}

export function useStaffLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; password: string }) => api.staffLogin(input),
    onSuccess: (session) => {
      // Seed the cache rather than refetching: the login response already
      // contains the user, and a second round trip on a slow connection is a
      // second chance to fail.
      queryClient.setQueryData(sessionKeys.me(), session.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      // Clear regardless of whether the API call succeeded. A user who pressed
      // "sign out" must end up signed out even if the network dropped.
      queryClient.setQueryData(sessionKeys.me(), null);
      queryClient.removeQueries({ queryKey: ['admin'] });
    },
  });
}
